import { X } from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaDateRangeField } from "@/shared/components/forms/ColocaDateRangeField";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaSelectField } from "@/shared/components/forms/ColocaSelectField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import useTranslations from "@/shared/lang/useTranslations";

import { DEFAULT_STATEMENT_FILTERS } from "../hooks/useWalletStatement";
import type { WalletStatementFilters as Filters } from "../types/payments-wallets.types";

interface WalletStatementFiltersProps {
  filters: Filters;
  onChange: (next: Filters) => void;
}

const filtersSchema = z.object({
  operation_type: z.enum(["all", "PAYIN", "PAYOUT", "SWAP", "FEE", "ADJUST", "REVERSE"]),
  direction: z.enum(["all", "C", "D"]),
  from: z.string(),
  to: z.string(),
});

/** Server-side statement filters: operation type, direction and posted_at day range (handoff §3.5). */
export const WalletStatementFilters = ({ filters, onChange }: WalletStatementFiltersProps) => {
  const { t } = useTranslations("wallets");
  const form = useZodForm(filtersSchema, filters);

  // Propagate every select change upstream so the list refetches (server-side).
  useEffect(() => {
    const subscription = form.watch((value) => onChange(value as Filters));
    return () => subscription.unsubscribe();
  }, [form, onChange]);

  const operationItems = [
    { value: "all", label: t("statement_filter_op_all") },
    { value: "PAYIN", label: t("tx_op_payin") },
    { value: "PAYOUT", label: t("tx_op_payout") },
    { value: "SWAP", label: t("tx_op_swap") },
    { value: "FEE", label: t("tx_op_fee") },
    { value: "ADJUST", label: t("tx_op_adjust") },
    { value: "REVERSE", label: t("tx_op_reverse") },
  ];
  const directionItems = [
    { value: "all", label: t("statement_filter_dir_all") },
    { value: "C", label: t("statement_filter_dir_credit") },
    { value: "D", label: t("statement_filter_dir_debit") },
  ];

  const hasFiltersActive =
    filters.operation_type !== "all" ||
    filters.direction !== "all" ||
    filters.from !== "" ||
    filters.to !== "";
  const today = new Date();

  return (
    <ColocaForm
      form={form}
      onSubmit={() => {
        // No-op: filters are live-applied via `form.watch`. The <form> only prevents Enter reloads.
      }}
      className="flex flex-col gap-2 space-y-0 rounded-lg border border-border bg-card p-3 sm:flex-row sm:flex-wrap sm:items-center"
    >
      <div className="w-full sm:w-[200px]">
        <ColocaSelectField name="operation_type" items={operationItems} />
      </div>
      <div className="w-full sm:w-[180px]">
        <ColocaSelectField name="direction" items={directionItems} />
      </div>
      <div className="w-full sm:w-[250px]">
        <ColocaDateRangeField
          nameFrom="from"
          nameTo="to"
          placeholder={t("statement_filter_range")}
          maxDate={today}
        />
      </div>
      {hasFiltersActive && (
        <ColocaButton
          type="button"
          variant="ghost"
          size="sm"
          icon={X}
          onClick={() => form.reset(DEFAULT_STATEMENT_FILTERS)}
        >
          {t("movements_filter_clear")}
        </ColocaButton>
      )}
    </ColocaForm>
  );
};
